import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useBusinessType } from '@/hooks/useBusinessType';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  LayoutDashboard, Kanban, Calendar, Settings,
  LogOut, Menu, ChevronDown, Shield, Building2, Upload, HelpCircle,
  CalendarCheck, Workflow, Plug, LayoutTemplate, AlertCircle, Users,
  GraduationCap, ClipboardList, Star, LifeBuoy, ListChecks, Gift
} from 'lucide-react';
import OnboardingTour from '@/components/OnboardingTour';
import TermsGate from '@/components/TermsGate';
import { AnnouncementBanner } from '@/components/AnnouncementsManager';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Layout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, setUser, logout, selectedCampaign, setSelectedCampaign, selectedBusiness, setSelectedBusiness } = useAuth();
  const [campaigns, setCampaigns] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [announcements, setAnnouncements] = useState([]);
  const [followupSummary, setFollowupSummary] = useState({ due_today: 0, overdue: 0, upcoming: 0, today_leads: [] });

  const fetchAnnouncements = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/announcements`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAnnouncements(data);
      }
    } catch (error) {
      console.error('Error fetching announcements:', error);
    }
  }, []);

  const fetchFollowupSummary = useCallback(async () => {
    if (!user) return;

    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const params = new URLSearchParams();

      if (selectedCampaign?.campaign_id) {
        params.set('campaign_id', selectedCampaign.campaign_id);
      } else if (user?.role === 'super_admin' && selectedBusiness?.business_id) {
        params.set('business_id', selectedBusiness.business_id);
      }

      const suffix = params.toString() ? `?${params.toString()}` : '';
      const response = await fetch(`${API_URL}/api/leads/followups/summary${suffix}`, {
        credentials: 'include',
        headers
      });

      if (response.ok) {
        setFollowupSummary(await response.json());
      }
    } catch (error) {
      console.error('Error fetching follow-up summary:', error);
    }
  }, [selectedBusiness?.business_id, selectedCampaign?.campaign_id, user]);

  const handleMarkAnnouncementRead = async (announcementId) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/announcements/${announcementId}/read`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      setAnnouncements(prev => 
        prev.map(a => a.announcement_id === announcementId ? { ...a, is_read: true } : a)
      );
    } catch (error) {
      console.error('Error marking announcement as read:', error);
    }
  };

  const persistSelectedBusiness = useCallback(async (businessId) => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_URL}/api/auth/select-business`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      },
      credentials: 'include',
      body: JSON.stringify({ business_id: businessId })
    });

    if (!response.ok) {
      throw new Error('Failed to persist selected business');
    }
  }, []);

  const fetchBusinesses = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      const response = await fetch(`${API_URL}/api/businesses`, {
        credentials: 'include',
        headers
      });

      if (response.ok) {
        const data = await response.json();
        setBusinesses(data);
        if (data.length === 0) {
          setSelectedBusiness(null);
          setSelectedCampaign(null);
          return;
        }

        const activeBusinessId = user?.current_business_id;
        const matchedBusiness = data.find((business) => business.business_id === activeBusinessId) || data[0];

        setSelectedBusiness(matchedBusiness);

        if (user?.role === 'super_admin' && user?.current_business_id !== matchedBusiness.business_id) {
          await persistSelectedBusiness(matchedBusiness.business_id);
          setUser((prev) => prev ? { ...prev, current_business_id: matchedBusiness.business_id } : prev);
        }
      }
    } catch (error) {
      console.error('Error fetching businesses:', error);
    }
  }, [persistSelectedBusiness, setSelectedBusiness, setSelectedCampaign, setUser, user?.current_business_id, user?.role]);

  const fetchCampaigns = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      const response = await fetch(`${API_URL}/api/campaigns`, {
        credentials: 'include',
        headers
      });

      if (response.ok) {
        const data = await response.json();
        setCampaigns(data);
        if (data.length > 0 && !selectedCampaign) {
          setSelectedCampaign(data[0]);
        }
      }
    } catch (error) {
      console.error('Error fetching campaigns:', error);
    }
  }, [selectedCampaign, setSelectedCampaign]);

  const fetchCampaignsForBusiness = useCallback(async (businessId) => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      const response = await fetch(`${API_URL}/api/campaigns?business_id=${businessId}`, {
        credentials: 'include',
        headers
      });

      if (response.ok) {
        const data = await response.json();
        setCampaigns(data);
        if (data.length > 0) {
          setSelectedCampaign(data[0]);
        } else {
          setSelectedCampaign(null);
        }
      }
    } catch (error) {
      console.error('Error fetching campaigns:', error);
    }
  }, [setSelectedCampaign]);

  useEffect(() => {
    if (!user) return;

    if (user.role === 'super_admin') {
      fetchBusinesses();
    } else {
      fetchCampaigns();
    }
    fetchAnnouncements();
    fetchFollowupSummary();
  }, [fetchAnnouncements, fetchBusinesses, fetchCampaigns, fetchFollowupSummary, user]);

  useEffect(() => {
    if (user?.role === 'super_admin' && selectedBusiness?.business_id) {
      fetchCampaignsForBusiness(selectedBusiness.business_id);
    }
  }, [fetchCampaignsForBusiness, selectedBusiness?.business_id, user?.role]);

  useEffect(() => {
    fetchFollowupSummary();
  }, [fetchFollowupSummary]);

  useEffect(() => {
    const handleRefresh = (event) => {
      const scope = event.detail?.scope;

      if (scope === 'businesses') {
        fetchBusinesses();
        return;
      }

      if (user?.role === 'super_admin' && selectedBusiness?.business_id) {
        fetchCampaignsForBusiness(selectedBusiness.business_id);
      } else {
        fetchCampaigns();
      }
    };

    window.addEventListener('beluga:data-refresh', handleRefresh);
    return () => window.removeEventListener('beluga:data-refresh', handleRefresh);
  }, [fetchBusinesses, fetchCampaigns, fetchCampaignsForBusiness, selectedBusiness?.business_id, user?.role]);

  const handleBusinessChange = async (businessId) => {
    const business = businesses.find(b => b.business_id === businessId);
    if (business) {
      try {
        await persistSelectedBusiness(businessId);
        setUser((prev) => prev ? { ...prev, current_business_id: businessId } : prev);
        setSelectedBusiness(business);
      } catch (error) {
        console.error('Error selecting business:', error);
      }
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleCampaignChange = (campaignId) => {
    const campaign = campaigns.find(c => c.campaign_id === campaignId);
    if (campaign) {
      setSelectedCampaign(campaign);
    }
  };

  const { isTrainingCentre } = useBusinessType();

  const trainingNavItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', tourId: 'nav-dashboard' },
    { path: '/student-pipeline', icon: Kanban, label: 'Pipeline', tourId: 'nav-student-pipeline' },
    { path: '/leads', icon: Users, label: 'Students', tourId: 'nav-students' },
    { path: '/courses', icon: GraduationCap, label: 'Courses', tourId: 'nav-courses' },
    { path: '/registrations', icon: ClipboardList, label: 'Registrations', tourId: 'nav-registrations' },
    { path: '/referrals', icon: Gift, label: 'Referrals', tourId: 'nav-referrals' },
    { path: '/reviews', icon: Star, label: 'Reviews', tourId: 'nav-reviews' },
    { path: '/manual-queue', icon: ListChecks, label: 'Manual Queue', tourId: 'nav-manual-queue' },
    { path: '/support', icon: LifeBuoy, label: 'Support', tourId: 'nav-support' },
    { path: '/settings', icon: Settings, label: 'Settings', tourId: 'nav-settings' },
  ];

  const generalNavItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', tourId: 'nav-dashboard' },
    { path: '/leads', icon: Users, label: 'Leads', tourId: 'nav-leads' },
    { path: '/pipeline', icon: Kanban, label: 'Pipeline', tourId: 'nav-pipeline' },
    { path: '/appointments', icon: CalendarCheck, label: 'Appointments', tourId: 'nav-appointments' },
    { path: '/followups', icon: Calendar, label: 'Follow-ups', tourId: 'nav-followups' },
    { path: '/workflows', icon: Workflow, label: 'Workflows', tourId: 'nav-workflows' },
    { path: '/funnels', icon: LayoutTemplate, label: 'Funnels', tourId: 'nav-funnels' },
    { path: '/forms', icon: LayoutTemplate, label: 'Forms', tourId: 'nav-forms' },
    { path: '/integrations', icon: Plug, label: 'Integrations', tourId: 'nav-integrations' },
    { path: '/upload', icon: Upload, label: 'Upload Leads', tourId: 'nav-upload' },
    { path: '/settings', icon: Settings, label: 'Settings', tourId: 'nav-settings' },
    { path: '/help', icon: HelpCircle, label: 'Help', tourId: 'nav-help' },
  ];

  const navItems = isTrainingCentre ? trainingNavItems : generalNavItems;

  if (user?.role === 'super_admin') {
    const settingsIdx = navItems.findIndex(item => item.path === '/settings');
    navItems.splice(settingsIdx, 0, { path: '/admin', icon: Shield, label: 'Admin', tourId: 'nav-admin' });
  }

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  // Get display name for the header
  const getDisplayName = () => {
    if (user?.role === 'super_admin' && selectedBusiness) {
      return selectedBusiness.name;
    }
    return user?.business_name || 'Beluga Pro';
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Mobile Overlay */}
      {isSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/20 z-40 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50
        w-64 bg-white border-r border-slate-200
        transform transition-transform duration-200 ease-in-out
        ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-slate-100">
            <div className="flex items-center gap-3">
              <img src="/logo192.png" alt="Sarum Digital" className="w-10 h-10 rounded-full shadow-md shadow-pink-300/40" />
              <div>
                <h1 className="font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {getDisplayName()}
                </h1>
                <p className="text-xs text-slate-500">Training Centre Dashboard</p>
              </div>
            </div>
          </div>

          {/* Business Selector (Super Admin Only) */}
          {user?.role === 'super_admin' && businesses.length > 0 && (
            <div className="p-4 border-b border-slate-100">
              <label className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2 block">
                <Building2 className="w-3 h-3 inline mr-1" />
                Business
              </label>
              <Select 
                value={selectedBusiness?.business_id || ''} 
                onValueChange={handleBusinessChange}
              >
                <SelectTrigger 
                  className="w-full bg-slate-50 border-slate-200"
                  data-testid="business-selector"
                >
                  <SelectValue placeholder="Select business" />
                </SelectTrigger>
                <SelectContent>
                  {businesses.map((business) => (
                    <SelectItem 
                      key={business.business_id} 
                      value={business.business_id}
                    >
                      {business.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Campaign Selector */}
          <div className="p-4 border-b border-slate-100">
            <label className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2 block">
              Campaign
            </label>
            <Select 
              value={selectedCampaign?.campaign_id || ''} 
              onValueChange={handleCampaignChange}
            >
              <SelectTrigger 
                className="w-full bg-slate-50 border-slate-200"
                data-testid="campaign-selector"
              >
                <SelectValue placeholder="Select campaign" />
              </SelectTrigger>
              <SelectContent>
                {campaigns.map((campaign) => (
                  <SelectItem 
                    key={campaign.campaign_id} 
                    value={campaign.campaign_id}
                    data-testid={`campaign-option-${campaign.campaign_id}`}
                  >
                    {campaign.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path || location.pathname.startsWith(`${item.path}/`);
              return (
                <button
                  key={item.path}
                  onClick={() => {
                    navigate(item.path);
                    setIsSidebarOpen(false);
                  }}
                  className={`sidebar-link w-full ${isActive ? 'active' : ''}`}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  data-tour={item.tourId}
                >
                  <Icon className="w-5 h-5" />
                  <span>{item.label}</span>
                </button>
              );
            })}
          </nav>

          {/* User Menu */}
          <div className="p-4 border-t border-slate-100">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-3 w-full p-2 rounded-lg hover:bg-slate-100 transition-colors">
                  <Avatar className="w-9 h-9">
                    <AvatarImage src={user?.picture} />
                    <AvatarFallback className="bg-gradient-to-br from-cyan-400 to-blue-600 text-white text-sm">
                      {getInitials(user?.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 text-left">
                    <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
                    <p className="text-xs text-slate-500 truncate capitalize">{user?.role?.replace('_', ' ')}</p>
                  </div>
                  <ChevronDown className="w-4 h-4 text-slate-400" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>My Account</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/settings')}>
                  <Settings className="w-4 h-4 mr-2" />
                  Settings
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                  <LogOut className="w-4 h-4 mr-2" />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile Header */}
        <header className="lg:hidden bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => setIsSidebarOpen(true)}
            className="p-2.5 hover:bg-slate-100 rounded-lg min-w-[44px] min-h-[44px] flex items-center justify-center"
            data-testid="mobile-menu-btn"
          >
            <Menu className="w-6 h-6 text-slate-700" />
          </button>
          <div className="flex items-center gap-2">
            <img src="/logo192.png" alt="" className="w-7 h-7 rounded-full" />
            <span className="font-bold text-slate-900">{getDisplayName()}</span>
          </div>
          <Avatar className="w-8 h-8">
            <AvatarImage src={user?.picture} />
            <AvatarFallback className="bg-gradient-to-br from-cyan-400 to-blue-600 text-white text-xs">
              {getInitials(user?.name)}
            </AvatarFallback>
          </Avatar>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-4 md:p-6 lg:p-8 overflow-auto">
          {/* Announcement Banner */}
          <AnnouncementBanner 
            announcements={announcements} 
            onMarkRead={handleMarkAnnouncementRead} 
          />
          {(followupSummary.due_today > 0 || followupSummary.overdue > 0) && (
            <div className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-amber-900">
              <div className="flex items-start gap-3">
                <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" />
                <div className="min-w-0">
                  <p className="text-sm font-semibold">
                    {followupSummary.due_today} follow-up{followupSummary.due_today === 1 ? '' : 's'} due today
                    {followupSummary.overdue > 0 ? ` and ${followupSummary.overdue} overdue` : ''}
                  </p>
                  {followupSummary.today_leads?.length > 0 && (
                    <p className="mt-1 text-sm text-amber-800">
                      Today: {followupSummary.today_leads.map((lead) => lead.name).join(', ')}
                    </p>
                  )}
                  <Button
                    variant="link"
                    className="mt-1 h-auto p-0 text-amber-700"
                    onClick={() => navigate('/followups')}
                  >
                    Open Follow-ups
                  </Button>
                </div>
              </div>
            </div>
          )}
          {children}
        </main>
      </div>

      {/* Onboarding Tour */}
      <OnboardingTour />
      <TermsGate />
    </div>
  );
};

export default Layout;
